import axiosInstance from "../../api/axios";

const InvoiceService = {
  getAllInvoices: async () => {
    // baseURL already includes /api — paths must not repeat "api/"
    const endpoints = ["/invoices", "/invoice"];
    let lastError = null;

    for (const endpoint of endpoints) {
      try {
        const response = await axiosInstance.get(endpoint);
        const body = response?.data ?? {};
        const data = Array.isArray(body)
          ? body
          : Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.invoices)
          ? body.invoices
          : [];

        return { success: true, data, source: endpoint };
      } catch (error) {
        lastError = error;
      }
    }

    return { success: false, data: [], error: lastError };
  },

  /**
   * Manually trigger invoice generation with fallback
   * @param {Object} options - Generation options
   * @param {number} options.labelId - Optional label ID (if not provided, generates for all labels)
   * @param {boolean} options.useQueue - Whether to use queue (default: false - synchronous processing)
   * @param {number|string} options.month - Calendar month 1–12 (with year) for monthly period, e.g. YouTube
   * @param {boolean} options.forceRegenerate - Whether to force regeneration (default: false)
   * @returns {Promise<Object>} Generation results
   */
  generateInvoices: async (options = {}) => {
    try {
      const { labelId, useQueue = false, forceRegenerate = false, quarter, year, month } = options;
      const payload = {
        label_id: labelId,
        use_queue: useQueue,
        force_regenerate: forceRegenerate
      };
      
      // Quarterly: quarter + year
      if (quarter && year) {
        payload.quarter = quarter;
        payload.year = year;
      } else if (year && month !== undefined && month !== null && month !== "") {
        // Single calendar month (e.g. YouTube monthly invoices)
        payload.year = year;
        payload.month = month;
      }
      
      const response = await axiosInstance.post("/invoices/generate", payload);
      return response.data;
    } catch (error) {
      console.error("Failed to generate invoices", error);
      throw error;
    }
  },

  /**
   * Check invoice generation status
   * @param {Object} options - Status check options
   * @param {number} options.labelId - Optional label ID
   * @param {string} options.monthYear - Month/Year in format "MM-YYYY" (default: current month)
   * @returns {Promise<Object>} Status information
   */
  checkGenerationStatus: async (options = {}) => {
    try {
      const { labelId, monthYear } = options;
      const params = new URLSearchParams();
      if (labelId) params.append('label_id', labelId);
      if (monthYear) params.append('month_year', monthYear);
      
      const response = await axiosInstance.get(`/invoices/generate/status?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Failed to check invoice generation status", error);
      throw error;
    }
  },

  // Add other invoice methods as needed
  getInvoices: async (id, type) => {
    try {
      const response = await axiosInstance.get(`/invoices/${id}/${type}`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch invoices", error);
      throw error;
    }
  },

  /**
   * Invoices for a releasing label (Accounting labels.id or label_red_id).
   * Scoped by invoice.label_id — not by artist home label alone.
   */
  getLabelInvoices: async (labelId) => {
    try {
      const response = await axiosInstance.get(`/label-invoices/${labelId}`);
      const raw = response.data?.invoices ?? response.data?.data ?? response.data;
      return {
        success: true,
        data: Array.isArray(raw) ? raw : [],
      };
    } catch (error) {
      console.error("Failed to fetch label invoices", error);
      return { success: false, data: [], error };
    }
  },
};

export default InvoiceService;

